import {
  ontologyFieldDefinitionSchema,
  ontologyFieldTypeSchema,
} from 'features/dynamicOntology'
import { z } from 'zod'

export enum FieldCategory {
  INPUT = 'input',
  QUESTION = 'question',
  FLAG = 'flag',
  OUTCOME = 'outcome',
}

export enum SourceType {
  DOCUMENT = 'document',
  FREETEXT = 'freetext',
}

export enum OutcomeStatus {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

/**
 * Schema for a property within an items definition (e.g. a column in an array-of-objects).
 * Mirrors the base field definition with an additional sortOrder for display ordering.
 */
export const ItemPropertySchema = z.object({
  name: z.string(),
  type: ontologyFieldTypeSchema,
  allowedValues: z.array(z.string()).optional(),
  description: z.string().optional(),
  /** Controls column display order — lower values appear first */
  sortOrder: z.number().optional(),
})

/**
 * Describes what an array field contains.
 * - Primitive arrays: `{ type: "string" }` or `{ type: "number" }`
 * - Object arrays: `{ type: "object", properties: { ... } }`
 */
export const ItemsSchema = z.object({
  type: ontologyFieldTypeSchema,
  properties: z.record(z.string(), ItemPropertySchema).optional(),
})

export type ItemProperty = z.infer<typeof ItemPropertySchema>
export type Items = z.infer<typeof ItemsSchema>

// Regular form input field - extends base ontology schema
const InputFieldSchema = ontologyFieldDefinitionSchema.extend({
  category: z.literal(FieldCategory.INPUT),
  defaultValue: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      z.array(z.number()),
      z.array(z.boolean()),
      z.array(z.record(z.string(), z.unknown())),
    ])
    .optional(),
  /**
   * Controls whether to show the source input UI next to this field.
   * When true, displays a dropdown to select document sources or enter free text.
   * Defaults to false.
   */
  includeSource: z.boolean().optional().default(false),
  /**
   * Optional tooltip configuration object.
   * When present, automatically shows an info icon next to the field.
   */
  tooltip: z
    .object({
      /**
       * The tooltip content format
       */
      format: z.enum(['markdown', 'html', 'text']).default('markdown'),
      /**
       * The actual tooltip content
       */
      content: z.string(),
      /**
       * Tooltip positioning preference
       */
      placement: z
        .enum([
          'top',
          'bottom',
          'left',
          'right',
          'topLeft',
          'topRight',
          'bottomLeft',
          'bottomRight',
        ])
        .default('top'),
      /**
       * Tooltip trigger behavior
       */
      trigger: z.enum(['hover', 'click', 'focus']).default('hover'),
    })
    .optional(),
  /**
   * Optional LLM prompt instruction for this field.
   * Used by AI agents (e.g. gong-agent-service) to understand what to produce for this field.
   * Intentionally separate from tooltip.content, which is for user-facing display.
   */
  prompt: z.string().optional(),
  /**
   * Optional weight for weighted scoring in computed sections.
   * Used by functions like computeWeightedBooleanScore.
   */
  /**
   * Schema for array element contents.
   * Use when type is 'array' to describe what each element looks like.
   */
  items: ItemsSchema.optional(),
  /**
   * When true, inverts the color coding for enum/tri-state fields.
   * Use for fields where high is semantically bad (e.g. severity, risk).
   * low → green, med → orange, high → red
   */
  invertedColors: z.boolean().optional(),
})

// Flowchart question node
const QuestionNodeSchema = z.object({
  category: z.literal(FieldCategory.QUESTION),
  name: z.string(),
  type: z.enum(['boolean', 'number', 'text', 'enum', 'date']),
  description: z.string().optional(),
  allowedValues: z.array(z.string()).optional(),
  defaultValue: z
    .union([z.string(), z.number(), z.boolean(), z.null()])
    .optional(),
})

// Flowchart flag node
// Note: defaultValue is unused for Flag nodes but included for type consistency
const FlagNodeSchema = z.object({
  category: z.literal(FieldCategory.FLAG),
  name: z.string(),
  severity: z.enum(['info', 'warning', 'error']),
  description: z.string().optional(),
  defaultValue: z.unknown().optional(),
})

// Flowchart outcome node
// Note: defaultValue is unused for Outcome nodes but included for type consistency
const OutcomeNodeSchema = z.object({
  category: z.literal(FieldCategory.OUTCOME),
  name: z.string(),
  result: z.unknown(),
  description: z.string().optional(),
  defaultValue: z.unknown().optional(),
  status: z
    .enum([
      OutcomeStatus.SUCCESS,
      OutcomeStatus.ERROR,
      OutcomeStatus.WARNING,
      OutcomeStatus.INFO,
    ])
    .optional()
    .default(OutcomeStatus.SUCCESS),
})

// TODO: Remove once we backfill all ontology fields with a category
// Preprocess to default missing category to INPUT for backwards compatibility
export const OntologyFieldSchema = z.preprocess(
  (val) => {
    if (typeof val === 'object' && val !== null && !('category' in val)) {
      return { ...val, category: FieldCategory.INPUT }
    }
    return val
  },
  z.discriminatedUnion('category', [
    InputFieldSchema,
    QuestionNodeSchema,
    FlagNodeSchema,
    OutcomeNodeSchema,
  ])
)

export type InputField = z.infer<typeof InputFieldSchema>
export type QuestionNode = z.infer<typeof QuestionNodeSchema>
export type FlagNode = z.infer<typeof FlagNodeSchema>
export type OutcomeNode = z.infer<typeof OutcomeNodeSchema>
export type OntologyField = z.infer<typeof OntologyFieldSchema>

// Flowchart-specific node types (excludes INPUT)
export type FlowchartNode = QuestionNode | FlagNode | OutcomeNode

/**
 * Document citation returned by the LLM alongside an extracted field value.
 * Carries the filename, page number, and verbatim excerpt the model used.
 */
export const DocumentCitationSchema = z.object({
  doc: z.string(),
  page: z.number().nullable().optional(),
  excerpt: z.string().nullable().optional(),
})

/**
 * Citation for a value sourced from APM component data rather than a document.
 */
export const ApmCitationSchema = z.object({
  doc: z.literal('APM component data'),
  component: z.string(),
  field: z.string(),
  excerpt: z.string().nullable().optional(),
})

/**
 * Union of all citation shapes the LLM may return.
 * null means the LLM produced a value but couldn't (or didn't) cite a source.
 */
export const LlmCitationSchema = z
  .union([DocumentCitationSchema, ApmCitationSchema])
  .nullable()

export type DocumentCitation = z.infer<typeof DocumentCitationSchema>
export type ApmCitation = z.infer<typeof ApmCitationSchema>
export type LlmCitation = z.infer<typeof LlmCitationSchema>

/**
 * The shape of llmValue as returned by gong-agent-service /assess/section.
 * The outer `value` field on FieldValue holds the unwrapped scalar; this
 * object is the immutable lineage snapshot (value + where it came from).
 */
export const LlmValueSchema = z.object({
  value: z.unknown(),
  citation: LlmCitationSchema.optional(),
})

export type LlmValue = z.infer<typeof LlmValueSchema>

/**
 * Source metadata for a field value - tracks where the value came from
 */
export const FieldValueSourceSchema = z
  .object({
    type: z.enum([SourceType.DOCUMENT, SourceType.FREETEXT]),
    value: z.string(), // document ID or free text
    documentName: z.string().optional(), // Display name for documents
    /** Backend-autofill only. */
    page: z.number().nullable().optional(),
    /** Backend-autofill only. */
    excerpt: z.string().nullable().optional(),
  })
  .nullable()

export type FieldValueSource = z.infer<typeof FieldValueSourceSchema>

/**
 * Ticket-resident provenance overlay (FFS data-model refactor). Keys are
 * STABLE field paths (see `stepComponents/ffs/inputProvenance.ts`) such as
 * `assetModel:{componentId}:design_pressure` or `ffsConfig:rsfa`. Values reuse
 * the existing `FieldValueSource` citation shape.
 *
 * ANNOTATION-ONLY: the value a key describes lives on the asset-model snapshot
 * or the ffs-configuration entry — never here. This map records only WHERE a
 * value came from, so it is never a second source of truth for the value. The
 * citation is USER-MAINTAINED: editing a value does not auto-update its source,
 * so a citation may lag its value until the user updates it (accepted product
 * limitation — there is deliberately no fingerprint/versioning/auto-invalidation).
 */
export const TicketInputProvenanceSchema = z.record(
  z.string(),
  FieldValueSourceSchema
)

export type TicketInputProvenance = z.infer<typeof TicketInputProvenanceSchema>

/**
 * All field values are wrapped with source tracking.
 * The `value` holds the actual data (primitive, array, object, etc.)
 * and `source` tracks where the value came from.
 */
export const FieldValueSchema = z.object({
  value: z.unknown(),
  /** Omitted e.g. by gong-agent autofill; treat like unknown provenance. */
  source: FieldValueSourceSchema.optional(),
  /**
   * Raw, deep-sanitized LLM output from gong-agent-service /assess/section.
   * Shape from gong-agent-service is `{ value, citation }` (see LlmValueSchema).
   * Typed as `unknown` for backward compat with older scalar llmValue shapes.
   * Optional for backward compat with tickets emitted before this field existed.
   */
  llmValue: z.unknown().optional(),
  /**
   * Explicitly set to `false` by the frontend after an AI autofill response
   * when the LLM returned null for this field — meaning the information
   * could not be found in the available documents. Absent (or true) when the
   * field was successfully extracted. Cleared when the user manually edits
   * the field (edit handlers call `wrapFieldValue` which does not carry this
   * flag forward).
   */
  aiExtracted: z.boolean().optional(),
  /**
   * Frontend-only. Set by useGongAgentAutofill when a field comes back as
   * null (aiExtracted: false) so the citation chip can tell the user which
   * documents were searched. Never persisted to Rolodex.
   */
  documentsSearched: z.array(z.string()).optional(),
})

/**
 * Generic TypeScript type for field values.
 * Use `FieldValue<T>` when you know the inner value type (e.g., `FieldValue<boolean>`).
 * Defaults to `unknown` for dynamic/untyped contexts.
 *
 * `llmValue` is typed as `unknown` rather than `T` because gong-agent-service
 * wraps it in a `{ value, citation }` envelope (see LlmValueSchema).
 */
export type FieldValue<T = unknown> = {
  value: T
  source?: FieldValueSource
  llmValue?: unknown
  aiExtracted?: boolean
  documentsSearched?: string[]
}

/**
 * Field values record - maps field IDs to their wrapped values.
 *
 * Use `FieldValues` for a per-scope slice (a single component's field map, or a top-level-only
 * ticket slice via `stripComponents`). The full ticket root is `TicketFieldValues` — see below.
 */
export const FieldValuesSchema = z.record(z.string(), FieldValueSchema)

export type FieldValues = Record<string, FieldValue>

/**
 * Per-component field-value map on the ticket root.
 * Shape: `{ [componentId]: { [fieldKey]: FieldValue } }`.
 */
export const ComponentFieldValuesMapSchema = z.record(
  z.string(),
  z.record(z.string(), FieldValueSchema)
)

export type ComponentFieldValuesMap = z.infer<
  typeof ComponentFieldValuesMapSchema
>

/**
 * Ticket-root fieldValues schema. Reserves `components` for the per-component nested map;
 * all other keys must satisfy `FieldValueSchema` (enforced by `.catchall`).
 */
export const TicketFieldValuesSchema = z
  .object({ components: ComponentFieldValuesMapSchema.optional() })
  .catchall(FieldValueSchema)

/**
 * Ticket-root fieldValues. Deliberately typed the same as `FieldValues` (flat
 * `Record<string, FieldValue>`) so the thousands of existing consumers that read the
 * ticket's top-level slots continue to type-check unchanged. The runtime shape MAY carry
 * a reserved `components` key whose value is a `ComponentFieldValuesMap`, not a
 * `FieldValue` — reach into it via `getComponentSubtree` / `setComponentSubtree` below
 * (both cast through `unknown`) rather than direct property access.
 *
 * A more accurate type would use a union index signature, but that widens
 * `fieldValues[fieldId]` at every non-FFS reader — silently forcing casts across the
 * codebase for a shape that only FFS-scoped code cares about. The design constraint
 * (per the refactor plan §Scope confirmation) is that non-FFS workflows must be
 * unaffected; keeping the type as `FieldValues` upholds that.
 */
export type TicketFieldValues = FieldValues

/**
 * Reserved key inside `TicketFieldValues` and `ontologyFields` that holds the nested
 * per-component subtree. Exported so cross-file consumers (`flattenOntologyFields`,
 * `workflowStepsApi` collision guard, hooks) can compare against a single constant
 * rather than hard-coding the string literal.
 */
export const COMPONENT_SUBTREE_KEY = 'components' as const

/**
 * Read the reserved `components` subtree off a `TicketFieldValues`. Returns `undefined`
 * when the ticket carries no per-component values. Uses `Object.hasOwn` so an inherited
 * `components` from a poisoned prototype chain cannot leak through.
 */
export function getComponentSubtree(
  fv: TicketFieldValues | undefined
): ComponentFieldValuesMap | undefined {
  if (!fv) return undefined
  if (!Object.hasOwn(fv, COMPONENT_SUBTREE_KEY)) return undefined
  return (fv as unknown as { components?: ComponentFieldValuesMap })[
    COMPONENT_SUBTREE_KEY
  ]
}

/**
 * Return a new `TicketFieldValues` with the reserved `components` subtree replaced by
 * `next`. Preserves every top-level field-value slot. Callers that want to remove the
 * subtree entirely should use `stripComponents` instead.
 */
export function setComponentSubtree(
  fv: TicketFieldValues,
  next: ComponentFieldValuesMap
): TicketFieldValues {
  const out: TicketFieldValues = { ...fv }
  ;(out as unknown as { components: ComponentFieldValuesMap })[
    COMPONENT_SUBTREE_KEY
  ] = next
  return out
}

/**
 * Peel the reserved `components` key off a ticket-root fieldValues so the remainder is a
 * clean `FieldValues`. Use at the ticket-scope read boundary (`useScopedFieldAccess({kind:
 * 'ticket'})`) to keep the nested subtree out of top-level form state.
 */
export function stripComponents(fv: TicketFieldValues): FieldValues {
  return omitComponentSubtree(fv) as FieldValues
}

/**
 * Generic, untyped counterparts of `getComponentSubtree` / `stripComponents`
 * for records that carry the reserved key but aren't `TicketFieldValues` --
 * notably a workflow's `globalContext.ontologyFields`, whose top level is
 * field DEFINITIONS with a nested definitions map under `components`. Same
 * `Object.hasOwn` guard so a poisoned prototype chain cannot leak an
 * inherited `components` value.
 */
export function readComponentSubtree(record: Record<string, unknown>): unknown {
  return Object.hasOwn(record, COMPONENT_SUBTREE_KEY)
    ? record[COMPONENT_SUBTREE_KEY]
    : undefined
}

/**
 * New record without the reserved `components` key. Filter over entries
 * (rather than destructure-and-discard) so no unused-var eslint suppression
 * is needed.
 */
export function omitComponentSubtree<T>(
  record: Record<string, T>
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(record).filter(([k]) => k !== COMPONENT_SUBTREE_KEY)
  )
}
